import flatpickr from 'flatpickr'
import english from 'flatpickr/dist/l10n/default'
import { French } from 'flatpickr/dist/l10n/fr'
import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../../constants'
import { Callback } from '../../../modules/callback'
import { Locale } from '../../../modules/locale'
import MainModule from '../../../modules/main'
import { parseInlineStyle } from '../../../modules/utils'
import { parseLocalDate } from './date-utils'

export class LvcDatepicker extends MinzeElement {
  reactive: Reactive = [['value', '', true], 'defaultPlaceholder']

  attrs: Attrs = [
    ['disabled', false],
    ['placeholder'],
    ['height', '48px'],
    ['error', false],
    'name',
    'id',
    ['default-value', ''],
    ['date-format', 'YYYY-MM-DD'],
    'label',
    ['padding', '12px 16px'],
    ['fontSize', '14px'],
    ['marginTop', ''],
    ['clear-button-enabled', false],
    'placeholder-color',
    ['inner-style', ''],
    ['on-change'],
    ['preset', ''],
    ['date-format-placeholder', true],
    ['position', 'auto'],
    ['background', ''],
    // When the picker has no value yet, open the calendar on this date's
    // month/day instead of today (e.g. an end-date picker jumps to the
    // start date so users don't accidentally pick a day before it).
    ['jump-to-date', ''],
    // Earliest selectable date; days before it are disabled (e.g. an end-date
    // picker cannot be set before the chosen start date).
    ['min-date', ''],
  ]

  static observedAttributes = [
    'inner-style',
    'placeholder',
    'height',
    'name',
    'id',
    'label',
    'date-format',
    'default-value',
    'padding',
    'error',
    'onChange',
    'min-date',
  ]

  fieldType: string = 'date'
  internalValue: string = ''
  flatpickrInstance: any
  isCalendarOpen: boolean = false

  async onReady() {
    const language = Locale.language
    const locale = language === 'fr' ? French : english
    const labelFormat = this.getDateFormats(language)
    const format = this.getDateFormatMoment(language)
    const injectedElement = this.select('#flatpickr-input') as HTMLElement | undefined

    this.initializeFlatpickr(
      locale,
      this.internalValue as string,
      this.value as string,
      format,
      labelFormat.flatpickr,
      injectedElement
    )
    setTimeout(() => {
      this.rerender()
    }, 0)

    this.defaultPlaceholder = await this.getPlaceholder()

    // Prevent mouse wheel from scrolling year input
    const yearInput = this?.flatpickrInstance?.calendarContainer?.querySelector('input.cur-year')

    if (yearInput) {
      yearInput.addEventListener('wheel', (event: WheelEvent) => {
        event.preventDefault()
      })
    }
  }

  // Parse a 'YYYY-MM-DD' string as a local date so the calendar view lands on
  // the intended day (new Date('YYYY-MM-DD') would parse as UTC midnight and
  // could shift to the previous day in negative timezones).
  // Minze casts attribute values, so a min-date/jump-to-date can reach us as a
  // non-string (e.g. an empty attribute cast to a boolean). Coerce at this
  // boundary and delegate to the pure, unit-tested parseLocalDate.
  parseAttrAsLocalDate(value: unknown): Date | null {
    return parseLocalDate(typeof value === 'string' ? value : '')
  }

  getDateFormatMoment(language: string) {
    if (language === 'fr') {
      return 'DD/MM/YYYY'
    }
    return 'MM/DD/YYYY'
  }

  // this function returns the date format for Intl (object) and flatpickr (string)
  getDateFormats(language: string): { intl: Intl.DateTimeFormatOptions; flatpickr: string } {
    if (language === 'fr') {
      return {
        intl: { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' },
        flatpickr: 'd/m/Y', // Flatpickr format for French
      }
    }

    return {
      intl: { month: '2-digit', day: '2-digit', year: 'numeric' },
      flatpickr: 'm/d/Y', // Flatpickr format for English
    }
  }

  formatDate(date: string): string {
    const dateObj = new Date(date)
    const formatOptions = this.getDateFormats(Locale.language)

    return dateObj.toLocaleDateString(Locale.language, formatOptions.intl)
  }

  async getPlaceholder() {
    let placeholder = this.getDateFormatMoment(Locale.language)
    if (placeholder === 'DD/MM/YYYY') {
      return 'JJ/MM/AAAA'
    }
    return 'MM/DD/YYYY'
  }

  initializeFlatpickr(
    locale: any,
    internalValue: string,
    defaultValue: string,
    format: string,
    labelFormat: string,
    injectedElement?: HTMLElement
  ) {
    let container = MainModule.getInstance().getDatepickerContainer() as HTMLElement | null
    if (!injectedElement) {
      return
    }
    if (this.flatpickrInstance) {
      // do nothing for the moment
    } else {
      this.flatpickrInstance = flatpickr(injectedElement as any, {
        locale: locale,
        dateFormat: labelFormat,
        position: this.position ? (this.position as any) : 'auto',
        appendTo: container || undefined,
        clickOpens: true,
        disableMobile: true,
        minDate: this.parseAttrAsLocalDate(this.minDate) || undefined,
        onChange: (selectedDates: Date[], dateStr: string, instance: any) => {
          const parsedDate = new Date(selectedDates[0]).toISOString().split('T')[0]
          this.value = parsedDate

          if (selectedDates.length < 0) {
            this.value = ''
          }
          this.cast('change', { value: this.value })
          Callback.call(this.onChange as string, this.value)
        },
        onOpen: (selectedDates: Date[], dateStr: string, instance: any) => {
          this.isCalendarOpen = true
          // With no value selected, flatpickr defaults the view to today. If a
          // jump-to-date is provided (e.g. the chosen start date), navigate the
          // calendar there so the initial cursor lands on the right month/day.
          if ((!this.value || this.value === '') && this.jumpToDate) {
            const target = this.parseAttrAsLocalDate(this.jumpToDate)
            if (target) instance.jumpToDate(target, false)
          }
          this.rerender()
        },
        onClose: (selectedDates: Date[], dateStr: string, instance: any) => {
          this.isCalendarOpen = false
          this.rerender()
        },
      })
    }
  }

  onRender() {
    const language = Locale.language
    const locale = language === 'fr' ? French : english
    const format = this.getDateFormatMoment(language)
    const labelFormat = this.getDateFormats(language)
    const injectedElement = this.select('#flatpickr-input') as HTMLElement | undefined

    this.initializeFlatpickr(
      locale,
      this.internalValue as string,
      (this.value as string) || '',
      format,
      labelFormat.flatpickr,
      injectedElement
    )
  }

  onAttributeChange = (name: string, oldValue: string, newValue: string) => {
    if (name === 'date-format') {
      this.dateFormat = newValue
    }

    if (name === 'placeholder') {
      this.placeholder = newValue
    }

    // The min-date can change after init (e.g. the user picks/changes the start
    // date), so push it onto the live flatpickr instance to disable earlier days.
    if (name === 'min-date' && this.flatpickrInstance && oldValue !== newValue) {
      const parsed = newValue ? parseLocalDate(newValue) : null
      this.flatpickrInstance.set('minDate', parsed || '')
    }

    if ((name === 'default-value' || name === 'value') && oldValue !== newValue) {
      let valueToSet = newValue
      let oldSetValue = oldValue

      if (newValue === null) {
        valueToSet = ''
      }

      if (oldValue === null) {
        oldSetValue = ''
      }

      if (oldValue === null) {
        oldValue = ''
      }

      if (oldSetValue !== valueToSet) {
        this.value = valueToSet
        this.internalValue = new Date(valueToSet).toLocaleDateString(Locale.language, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        })

        //only value can update input directly if changed from outside
        //or else you risk forever loop
        const currentElement = this.select(`input`)
        if (currentElement as any) {
          ;(currentElement as any).value = valueToSet
        }
      }
    }

    if ((name === 'default-value' || name === 'value') && oldValue !== newValue) {
    }

    this.paddingValue = this.padding ? this.padding : '12px 16px'
    this.fontSizeValue = this.fontSize ? this.fontSize : '14px'
    this.heightValue = this.height ? this.height : '48px'
  }

  onBlur(e: Event) {
    setTimeout(() => {
      this.rerender()
    }, 0)
  }

  html = () => {
    let fieldType = this.fieldType
    let showError = typeof this.error === 'string' && this.error.length > 0
    let val = ''

    if (this.value !== '') {
      val = this.formatDate(this.value as string)
    }
    // const placeholder = this.getPlaceholder();

    if (this.preset === 'dropdown') {
      return `
      <lvc-box>
      ${this.label ? `<label for="${this.id}">${this.label}</label>` : ''}
      <div class="input-container">
        <input
          data-input
          ${this.disabled ? `disabled` : ''}
          placeholder="${this.placeholder ? this.placeholder : this.defaultPlaceholder}"
          type="text"
          data-main-input="true"
          name="${this.name}"
          id="flatpickr-input"
          pattern="${this.pattern || ''}"
          ${val ? `value="${val}"` : ''}
          class="input ${this.error ? 'error' : ''}"
          readonly="readonly"
        >
        ${this.isCalendarOpen ? `<lvc-chevron-up-icon class="icon input-button"></lvc-chevron-up-icon>` : `<lvc-chevron-down-icon class="icon input-button"></lvc-chevron-down-icon>`}
      </div>
    </lvc-box>
    ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      `
    }

    return `
      <lvc-box>
        ${this.label ? `<label for="${this.id}">${this.label}</label>` : ''}

        <div class="input-container">
          <input
          data-input
          ${this.disabled ? `disabled` : ''}
          placeholder="${this.placeholder ? this.placeholder : this.defaultPlaceholder}"
          type="text"
          data-main-input="true"
          name="${this.name}"
          id="flatpickr-input"
          pattern="${this.pattern || ''}"
          ${val ? `value="${val}"` : ''}
          class="input ${this.error ? 'error' : ''}"
          
          readonly="readonly"
          />
          <a class="input-button button" title="toggle" data-toggle>
            <lvc-calendar-icon class="icon"></lvc-calendar-icon>
          </a>
         
          <a class="clear-input-button button" title="clear" >
           <lvc-clear-input-icon class="icon"></lvc-clear-input-icon> 
          </a>
        </div>
      </lvc-box>
      ${showError ? `<lvc-error error="${this.error}"></lvc-error>` : ''}
      `
  }

  css = () => {
    if (this.preset === 'dropdown') {
      return `:host {
        width: 100%;
        position: relative;
        box-sizing: border-box;
        display: block;
      }
  
      .icon{
        display:flex;
        position: absolute;
        right: 16px;
        cursor: pointer;
      }
     
      #flatpickr-input:focus-visible {
        outline: unset;
        background: #BFF0FF
      }
      .input-container {
        position: relative;
        display:flex;
        align-items: center;
      }
  
      .button {
        cursor:pointer;
        height: 48px;
        display: flex;
        align-items: center;
        background-color: #fff;
        padding: 0 5px;
      }
  
      #flatpickr-input {
        width: 100%;
        background: transparent;
        ${this.heightValue ? `height: ${this.heightValue};` : ''}
        ${this.fontSizeValue ? `font-size: ${this.fontSizeValue};` : ''}
        ${this.paddingValue ? `padding: ${this.paddingValue};` : ''}
        ${this.isCalendarOpen ? `background: #BFF0FF;` : `background: ${this.background || '#BFF0FF'};`}
        color: #1E007D;
        ${this.marginTop ? `margin-top: ${this.marginTop};` : ''}
        border-radius: 99px;
        cursor: pointer;
        flex: 1;
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      ::placeholder {
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%;
        color: #1E007D;
      }
  
      label {
        color: #1E007D;
        font-size: 14px;
        line-height: 17px;
        font-weight: ${Font.weight.medium};
      }
  
      .error {
        background: #FFEBD7;
        border-bottom: 3px solid #FF4600;
      }
  
      .flatpickr-months .flatpickr-month {
        display: flex;
        align-items: center;
      }
      
      .flatpickr-months .flatpickr-prev-month, .flatpickr-months .flatpickr-next-month {
        padding: 0 10px;
        display: flex;
        align-items: center;
      }
      
      .flatpickr-current-month {
        width: 100%;
        position: static;
        padding: 0;
        height: auto;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      
      .flatpickr-current-month span.cur-month:hover, .numInputWrapper:hover {
        background: none;
      }
      
      .flatpickr-current-month span.cur-month, .flatpickr-current-month input.cur-year {
        font-weight: ${Font.weight.medium};
        font-size: 17px;
        line-height: 120%;
        color: #1E007D;
        text-transform: capitalize;
      }
      
      .flatpickr-current-month .numInputWrapper {
        width: 50px;
        display: flex;
        align-items: center;
      }
      
      .numInputWrapper span.arrowUp, .numInputWrapper span.arrowDown {
        display: none;
      }
      
      .dayContainer, .flatpickr-days, .flatpickr-calendar {
        width: 336px;
        box-shadow: 0px 4px 16px 0px rgba(15, 0, 62, 0.15);
      }
  
      .flatpickr-wrapper {
        width:100%;
      }
      
      .flatpickr-days {
        margin-top: 12px;
      }
      
      .dayContainer {
        max-width: 336px;
        min-width: 336px;
        row-gap: 4px;
        height: calc(5 * 48px + 16px);
        overflow: hidden;
      }
      
      .flatpickr-day {
        border: none;
        margin: 0;
        max-width: none;
        width: 48px;
        height: 48px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-weight: ${Font.weight.medium};
        font-size: 14px;
        line-height: 16px;
        color: #1E007D;
      }
      
      .flatpickr-day:hover, .flatpickr-day.today:hover {
        background: #1E007D;
        border-radius: 8px;
        color: #FFFFFF;
      }
      
      .flatpickr-day.today.selected:hover, .flatpickr-day.today.endRange:hover, .flatpickr-day.today.startRange:hover {
        color: #FFFFFF;
        background: #1E007D;
      }
      
      .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
      .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
      .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
        box-shadow: none;
      }
      
      .flatpickr-day.inRange, .flatpickr-day.today.inRange {
        background: #BFF0FF;
        box-shadow: none;
      }
      
      .flatpickr-day.inRange:hover {
        border-radius: 0;
      }
      
      .rangeMode .flatpickr-day {
        margin-top: 0;
      }
      
      .flatpickr-day.selected {
        background: #1E007D;
      }
      
      .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.startRange:hover, .flatpickr-day.endRange:hover {
        background: #1E007D;
      }
      
      .flatpickr-day.selected,
      .flatpickr-day.startRange,
      .flatpickr-day.endRange,
      .flatpickr-day.selected.inRange,
      .flatpickr-day.startRange.inRange,
      .flatpickr-day.endRange.inRange,
      .flatpickr-day.selected:focus,
      .flatpickr-day.startRange:focus,
      .flatpickr-day.endRange:focus,
      .flatpickr-day.selected:hover,
      .flatpickr-day.startRange:hover,
      .flatpickr-day.endRange:hover,
      .flatpickr-day.selected.prevMonthDay,
      .flatpickr-day.startRange.prevMonthDay,
      .flatpickr-day.endRange.prevMonthDay,
      .flatpickr-day.selected.nextMonthDay,
      .flatpickr-day.startRange.nextMonthDay,
      .flatpickr-day.endRange.nextMonthDay {
        background: #1E007D;
      }
      
      .flatpickr-day.selected, .flatpickr-day.startRange {
        border-color: #1E007D;
      }
      
      .flatpickr-day.selected.startRange, .flatpickr-day.startRange.startRange, .flatpickr-day.endRange.startRange {
        border-radius: 8px 0px 0px 8px;
      }
      
      .flatpickr-day.selected.endRange, .flatpickr-day.startRange.endRange, .flatpickr-day.endRange.endRange {
        border-radius: 0px 8px 8px 0px;
      }
      
      .flatpickr-day.flatpickr-disabled, 
      .flatpickr-day.flatpickr-disabled:hover, 
      .flatpickr-day.prevMonthDay, 
      .flatpickr-day.nextMonthDay, 
      .flatpickr-day.notAllowed, 
      .flatpickr-day.notAllowed.prevMonthDay, 
      .flatpickr-day.notAllowed.nextMonthDay {
        font-weight: ${Font.weight.medium};
        font-size: 14px;
        line-height: 16px;
        color: rgba(30, 0, 125, 0.3);
      }
      
      .flatpickr-weekdays {
        border-bottom: 1px solid rgba(30, 0, 125, 0.3);
        height: auto;
        padding: 16px 0 12px 0;
      }
      
      span.flatpickr-weekday {
        font-weight: ${Font.weight.medium};
        font-size: 12px;
        line-height: 120%;
        color: #1E007D;
      }
  
      .drawer-content .flatpickr-calendar {
        background: transparent;
        box-shadow: 0px 4px 16px 0px rgba(15, 0, 62, 0.15);;
      }
  
  
      // dropdown styles 
  
  
      .overlay {
        position: fixed;
        top: 0px;
        left: 0px;
        bottom: 0px;
        right: 0px;
        width: 100%;
        height: 100%;
        z-index: 1;
        display: none;
      }
      .overlay.show {
        display: block;
      }
  
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 16px;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        border-top-left-radius: 24px;
        border-top-right-radius: 24px;
        border-bottom: 1px solid var(--Minuit, #1E007D);
        background: var(--Ciel, #BFF0FF);
  
      }
  
  
      input[name="filter"] {
        width: 100%;
        height: 48px;
        border: none;
        outline: none;
        padding: 0 0;
        box-sizing: border-box;
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
        color: var(--Minuit, #1E007D);
        background: transparent;
      }
  
      .title {
        color: var(--Minuit, #1E007D);
        font-variant-numeric: lining-nums tabular-nums;
        font-feature-settings: 'clig' off, 'liga' off;
  
        /* Body 3 */
        font-family: ${Font.family};
        font-size: 14px;
        font-style: normal;
        font-weight: ${Font.weight.medium};
        line-height: 150%; /* 21px */
      }
  
      .input {
        display: flex;
        height: 48px;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;
        flex-shrink: 0;
        align-self: stretch;
        border: 1px solid var(--Minuit, #1E007D);
        position: relative;
        border-radius: 24px;
      }
  
      .disciplines {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: stretch;
        overflow: hidden;
        overflow-y: auto;
        max-height: calc(300px - 48px);
        padding: 0 10px;
      }
  
  
      .disciplines button {
        display: flex;
        justify-content: space-between;
        font-family: ${Font.family};
        font-size: 14px;
        line-height: 32px;
        cursor: pointer;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        padding: 8px 8px;
        border: none;
        height: 48px;
        flex: 0 0 48px;
        border-bottom: 1px solid rgba(30, 0, 125, 0.30);
      }
  
      .disciplines button:last-child {
        border-bottom: none;
      }
  
      .disciplines button.selected {
      }
  
  
      /* style.css */
  
      /* Dropdown Button */
      .dropbtn {
        color: white;
        padding: 16px;
        font-size: 16px;
        border: none;
        cursor: pointer;
        flex: 1;
        width: 100%;
        background-color: transparent;
        color: var(--Minuit, #1E007D);
        text-align: left;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
  
      /* Dropdown button on hover & focus */
      .dropbtn:hover, .dropbtn:focus {
      }
  
      /* Dropdown Content */
      .dropdown-content {
        width: 100%;
        display: none;
        position: absolute;
        background-color: #f9f9f9;
        min-width: 160px;
        box-shadow: 0px 8px 16px 0px rgba(0,0,0,0.2);
        z-index: 2;
        top: 0;
        border-radius: 24px; 
        max-height: 300px;
        min-height: 96px;
        border: 1px solid var(--Minuit, #1E007D);
        line-height: 32px;
      }
  
      .dropdown-content.show {
        display: block;
      }`
    }

    return `
    :host {
      width: 100%;
      position: relative;
      box-sizing: border-box;
      display: block;
    }

    .icon{
      display:flex;
    }
   
    #flatpickr-input:focus-visible {
      outline: unset;
    }
    .input-container {
      position: relative;
      display:flex;
      align-items: center;
    }

    .button {
      cursor:pointer;
      height: 48px;
      display: flex;
      align-items: center;
      background-color: #fff;
      padding: 0 5px;

    }
    
    .clear-input-button {
      border-radius: 0 60px 60px 0;
    }

    #flatpickr-input {
      width: 100%;
      border: unset;
      ${this.heightValue ? `height: ${this.heightValue};` : ''}
      ${this.fontSizeValue ? `font-size: ${this.fontSizeValue};` : ''}
      ${this.paddingValue ? `padding: ${this.paddingValue};` : ''}
      background: #fff;
      color: #1E007D;
      ${this.marginTop ? `margin-top: ${this.marginTop};` : ''}
      border-radius: 60px 0 0 60px;
      cursor: pointer;
    }
    
    ${
      this.placeholderColor
        ? `
      ::placeholder {
        color: ${this.placeholderColor};
        opacity: 1;
      }
      
    `
        : ``
    }

    label {
      color:  #1E007D;
      font-size: 14px;
      line-height: 17px;
      font-weight: ${Font.weight.bold};
    }

    .error {
      background: #FFEBD7;
      border-bottom: 3px solid #FF4600;
    }

    .flatpickr-months .flatpickr-month {
      display: flex;
      align-items: center;
    }
    
    .flatpickr-months .flatpickr-prev-month, .flatpickr-months .flatpickr-next-month {
      padding: 0 10px;
      display: flex;
      align-items: center;
    }
    
    .flatpickr-current-month {
      width: 100%;
      position: static;
      padding: 0;
      height: auto;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    
    .flatpickr-current-month span.cur-month:hover, .numInputWrapper:hover {
      background: none;
    }
    
    .flatpickr-current-month span.cur-month, .flatpickr-current-month input.cur-year {
      font-weight: ${Font.weight.medium};
      font-size: 17px;
      line-height: 120%;
      color: #1E007D;
      text-transform: capitalize;
    }
    
    .flatpickr-current-month .numInputWrapper {
      width: 50px;
      display: flex;
      align-items: center;
    }
    
    .numInputWrapper span.arrowUp, .numInputWrapper span.arrowDown {
      display: none;
    }
    
    .dayContainer, .flatpickr-days, .flatpickr-calendar {
      width: 336px;
      border-radius: 16px!important;
    }

    .flatpickr-wrapper {
      width:100%;
    }
    
    .flatpickr-days {
      margin-top: 12px;
    }
    
    .dayContainer {
      max-width: 336px;
      min-width: 336px;
      row-gap: 4px;
      height: calc(5 * 48px + 16px);
      overflow: hidden;
    }
    
    .flatpickr-day {
      border: none;
      margin: 0;
      max-width: none;
      width: 48px;
      height: 48px;
      display: flex;
      justify-content: center;
      align-items: center;
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: #1E007D;
    }
    
    .flatpickr-day:hover, .flatpickr-day.today:hover {
      background: #BFF0FF;
      border-radius: 8px;
      color: #1E007D;
    }
    
    .flatpickr-day.today.selected:hover, .flatpickr-day.today.endRange:hover, .flatpickr-day.today.startRange:hover {
      color: #FFFFFF;
      background: #BFF0FF;
    }
    
    .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n+1)),
    .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n+1)) {
      box-shadow: none;
    }
    
    .flatpickr-day.inRange, .flatpickr-day.today.inRange {
      background: #BFF0FF;
      box-shadow: none;
    }
    
    .flatpickr-day.inRange:hover {
      border-radius: 0;
    }
    
    .rangeMode .flatpickr-day {
      margin-top: 0;
    }
    
    .flatpickr-day.selected {
      background: #1E007D;
    }
    
    .flatpickr-day.startRange, .flatpickr-day.endRange, .flatpickr-day.startRange:hover, .flatpickr-day.endRange:hover {
      background: #1E007D;
    }
    
    .flatpickr-day.selected,
    .flatpickr-day.startRange,
    .flatpickr-day.endRange,
    .flatpickr-day.selected.inRange,
    .flatpickr-day.startRange.inRange,
    .flatpickr-day.endRange.inRange,
    .flatpickr-day.selected:focus,
    .flatpickr-day.startRange:focus,
    .flatpickr-day.endRange:focus,
    .flatpickr-day.selected:hover,
    .flatpickr-day.startRange:hover,
    .flatpickr-day.endRange:hover,
    .flatpickr-day.selected.prevMonthDay,
    .flatpickr-day.startRange.prevMonthDay,
    .flatpickr-day.endRange.prevMonthDay,
    .flatpickr-day.selected.nextMonthDay,
    .flatpickr-day.startRange.nextMonthDay,
    .flatpickr-day.endRange.nextMonthDay {
      background: #1E007D;
    }
    
    .flatpickr-day.selected, .flatpickr-day.startRange {
      border-color: #1E007D;
    }
    
    .flatpickr-day.selected.startRange, .flatpickr-day.startRange.startRange, .flatpickr-day.endRange.startRange {
      border-radius: 8px 0px 0px 8px;
    }
    
    .flatpickr-day.selected.endRange, .flatpickr-day.startRange.endRange, .flatpickr-day.endRange.endRange {
      border-radius: 0px 8px 8px 0px;
    }
    
    .flatpickr-day.flatpickr-disabled, 
    .flatpickr-day.flatpickr-disabled:hover, 
    .flatpickr-day.prevMonthDay, 
    .flatpickr-day.nextMonthDay, 
    .flatpickr-day.notAllowed, 
    .flatpickr-day.notAllowed.prevMonthDay, 
    .flatpickr-day.notAllowed.nextMonthDay {
      font-weight: ${Font.weight.medium};
      font-size: 14px;
      line-height: 16px;
      color: rgba(30, 0, 125, 0.3);
    }
    
    .flatpickr-weekdays {
      border-bottom: 1px solid rgba(30, 0, 125, 0.3);
      height: auto;
      padding: 16px 0 12px 0;
    }
    
    span.flatpickr-weekday {
      font-weight: ${Font.weight.medium};
      font-size: 12px;
      line-height: 120%;
      color: #1E007D;
    }

    .drawer-content .flatpickr-calendar {
      background: transparent;
      border-radius: 16px!important;
    }

 
    ${parseInlineStyle(this.innerStyle as string)}
  `
  }

  clearInput = () => {
    if (this.value === '') return

    this.value = ''

    const currentElement = this.select(`input`)
    if (currentElement as any) {
      ;(currentElement as any).value = ''
    }
    this.rerender()
  }

  onIconClick(e: Event) {
    if (this.flatpickrInstance) {
      this.flatpickrInstance.open()
    }
  }

  eventListeners: EventListeners = [
    ['input', 'blur', this.onBlur.bind(this)],
    ['.input-button', 'click', this.onIconClick.bind(this)],
    ['.clear-input-button', 'click', this.clearInput.bind(this)],
  ]
}
